import clsx from 'clsx';
import { Category } from 'olo-service';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from 'components/Icon';
import { Drawer } from 'components/Drawer/Drawer';

import style from './style.module.scss';
import { CATEGORY_MENU_HEADER_ID, CATEGORY_MENU_TRIGGER_ID } from './const';
import { useHandleCloseDrawer } from 'hooks/useHandleCloseDrawer';

type TableOfContentProps = {
  categories: Category[];
};

/**
 * Drawer that displays a list of all menu categories
 */
export const TableOfContent: FC<TableOfContentProps> = ({ categories }) => {
  const { t } = useTranslation();

  const tableOfContetWrapperRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  const toggleDrawer = useCallback(() => setIsOpen(current => !current), []);

  // Handle closing on outside mouse clicks
  useHandleCloseDrawer(tableOfContetWrapperRef, () => { setIsOpen(false); });

  const initialCategoryName = t('categories');
  const [currentCategoryName, setCurrentCategoryName] = useState(initialCategoryName);

  useEffect(() => {
    const categoryIds = categories.map(category => category.id);
    const categoriesInView = new Map();
    categoryIds.forEach(categoryId => categoriesInView.set(categoryId, false));

    const handleIntersection = function (entries: IntersectionObserverEntry[]) {
      entries.forEach((entry) => {
        categoriesInView.set(entry.target.id, entry.isIntersecting);

        const categoryId = categoryIds[Array.from(categoriesInView.values()).lastIndexOf(true)];
        setCurrentCategoryName(categories.find(category => category.id === categoryId)?.name || initialCategoryName);
      });
    };

    const observer = new IntersectionObserver(handleIntersection, { rootMargin: '-20% 0px -80% 0px' });

    categoryIds.forEach(categoryId => {
      const element = document.getElementById(categoryId);
      element && observer.observe(element);
    });

    return () => observer.disconnect();
  }, [categories, initialCategoryName, setCurrentCategoryName]);

  if (categories.length < 1) return null;

  return (
    <div className={style.wrapper} ref={tableOfContetWrapperRef}>
      <button
        id={CATEGORY_MENU_TRIGGER_ID}
        className={style.drawerToggle}
        onClick={toggleDrawer}
        aria-expanded={isOpen}
      >
        <span>{currentCategoryName}</span>
        <Icon className={clsx(style.icon, isOpen && style.openIcon)} name={'expand'} />
      </button>
      <Drawer
        isOpen={isOpen}
        triggerElement={CATEGORY_MENU_TRIGGER_ID}
        className={style.drawer}
        onClose={() => setIsOpen(false)}
      >
        <nav aria-labelledby={CATEGORY_MENU_HEADER_ID}>
          <h2 id={CATEGORY_MENU_HEADER_ID} className="label">{t('categories')}</h2>
          <ul>
            {categories.map(option => (
              <li key={option.id}>
                <a className="p" onClick={() => {
                  setIsOpen(false);
                  setCurrentCategoryName(option.name);
                }} href={`#${option.id}`}>
                {option.name}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </Drawer>
    </div>
  );
};
